/* -------------------------- Design imports start -------------------------- */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  List,
  ListItem,
  ListItemButton,
  Typography,
} from '@mui/material'
import Drawer from '@mui/material/Drawer'
/* --------------------------- Design imports end --------------------------- */
/* ------------------------ Functional imports start ------------------------ */
import { type KeyboardEvent, type ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { useUserContext } from '../../utils/context'
import { canView, isPrivileged } from '../../utils/functions'
import { type FeatureKey, isFeatureEnabled } from '../../config/featureFlags'
import { PERMISSIONS } from '../../config/permissions'
/* ------------------------- Functional imports end ------------------------- */

const drawerWidth = 230

type SideBarItem = {
  id: number
  name: string
  path: string
  permission?: string | string[]
  feature?: FeatureKey | FeatureKey[]
  subMenu?: SideBarItem[]
}

type Props = {
  openSidebar: boolean
}
/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function SideBar(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const { openSidebar } = props
  const { t } = useTranslation(['common', 'contact'])
  const { userPermissions, user } = useUserContext()
  const isAllowed = (permission?: string | string[]): boolean => {
    if (isPrivileged(user)) {
      return true
    }
    if (!permission) {
      return true
    }
    if (Array.isArray(permission)) {
      return permission.some(perm => canView(userPermissions, perm))
    }
    return canView(userPermissions, permission)
  }

  const isFeatureVisible = (feature?: FeatureKey | FeatureKey[]): boolean => {
    if (!feature) {
      return true
    }
    const features = Array.isArray(feature) ? feature : [feature]
    return features.some(currentFeature => isFeatureEnabled(currentFeature))
  }

  const filterSideBarItems = (items: SideBarItem[]): SideBarItem[] =>
    items
      .map(item => {
        const subMenu = item.subMenu ? filterSideBarItems(item.subMenu) : undefined
        return {
          ...item,
          subMenu,
        }
      })
      .filter(item => isAllowed(item.permission) && isFeatureVisible(item.feature))
      .filter(item => (item.subMenu ? item.subMenu.length > 0 : true))

  const sideBarItems: SideBarItem[] = useMemo(
    () =>
      filterSideBarItems([
        {
          id: 0,
          name: t('common:content.label.item'),
          path: '/items/landingpage',
          permission: [
            PERMISSIONS.ITEMS.ARTICLE,
            PERMISSIONS.ITEMS.BOM_BASE,
            PERMISSIONS.LOGISTICS.STORAGE_ARTICLE,
          ],
          feature: 'items',
          subMenu: [
            {
              id: 0,
              name: t('content.label.allItems'),
              path: '/items',
              permission: PERMISSIONS.ITEMS.ARTICLE,
              feature: 'items',
            },
            {
              id: 1,
              name: t('content.label.boms'),
              path: '/items/bom/overview',
              permission: PERMISSIONS.ITEMS.BOM_BASE,
              feature: 'bom',
            },
            {
              id: 2,
              name: t('content.label.stock'),
              path: '/items/itemstock',
              permission: PERMISSIONS.LOGISTICS.STORAGE_ARTICLE,
              feature: 'logistics',
            },
            {
              id: 3,
              name: t('sharepoint:content.label.shares'),
              path: '/items/sharepoints',
              permission: PERMISSIONS.ITEMS.SHAREPOINT,
              feature: 'sharepoint',
            },
          ],
        },
        {
          id: 1,
          name: t('permissions:label.employeesAndRoles', 'Mitarbeiter & Rollen'),
          path: '/personnel/landingpage',
          permission: ['accounts:user', 'accounts:role', 'organizations:organization'],
          feature: ['personnel', 'roles'],
          subMenu: [
            {
              id: 0,
              name: t('content.label.employee'),
              path: '/personnel/employees/list',
              permission: 'accounts:user',
              feature: 'personnel',
            },
            {
              id: 1,
              name: t('content.label.roles'),
              path: '/personnel/roles/list',
              permission: 'accounts:role',
              feature: 'roles',
            },
            {
              id: 2,
              name: t('content.loginForm.company'),
              path: '/personnel/organization',
              permission: 'organizations:organization',
              feature: 'personnel',
            },
          ],
        },
        {
          id: 2,
          name: t('content.label.orderControl'),
          path: '/contacts/customer/list',
          permission: [
            'customer:customer',
            'supplier:supplier',
            'customer:production_order',
            'supplier:supply_order',
            'customer:customer_order',
          ],
          feature: ['contacts', 'customer', 'supplier'],
          subMenu: [
            {
              id: 0,
              name: t('common:content.label.contacts'),
              path: '/contacts',
              permission: 'contacts:contact',
              feature: 'contacts',
            },
            {
              id: 0,
              name: t('contact:content.label.contactGroups'),
              path: '/contact_groups',
              permission: 'contacts:contact_groups',
              feature: 'contacts',
            },
            {
              id: 1,
              name: t('common:content.label.customerList'),
              path: '/contacts/customer/list',
              permission: 'customer:customer',
              feature: 'customer',
            },
            {
              id: 2,
              name: t('common:content.label.supplierList'),
              path: '/contacts/supplier/list',
              permission: 'supplier:supplier',
              feature: 'supplier',
            },
            {
              id: 3,
              name: t('common:content.label.customerOrders'),
              path: '/contacts/customer/orders',
              permission: 'customer:customer_order',
              feature: ['customer'],
            },
            {
              id: 4,
              name: t('common:content.label.supplierOrders'),
              path: '/contacts/supplier/orders',
              permission: 'supplier:supply_order',
              feature: ['supplier'],
            },
            {
              id: 5,
              name: t('common:content.label.productionOrders'),
              path: '/contact/supplier/productionOrder',
              permission: 'customer:production_order',
              feature: 'customer',
            },
          ],
        },
        {
          id: 3,
          name: t('common:content.label.contact'),
          path: '/support/contact',
          permission: 'support:ticket',
          feature: 'support',
        },
      ]),
    [t, userPermissions, user]
  )
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [open, setOpen] = useState(true)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const [screenSize, setScreenSize] = useState(window.innerWidth)
  const sidebarRef = useRef<any>(null)
  const history = useHistory()
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  useEffect(() => {
    window.addEventListener('resize', updateScreenSize)
    return () => window.removeEventListener('resize', updateScreenSize)
  }, [])

  useEffect(() => {
    const handleResize = () => {
      if (screenSize < 1600) {
        setOpen(false)
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [sideBarItems])

  useEffect(() => {
    const adjustSidebarHeight = (): void => {
      const sidebarElement = sidebarRef.current
      if (sidebarElement) {
        const windowHeight = window.innerHeight
        const sidebarTopOffset = sidebarElement.getBoundingClientRect().top
        const newSidebarHeight = windowHeight - sidebarTopOffset
        sidebarElement.style.height = `${newSidebarHeight}px`
      }
    }

    adjustSidebarHeight()
    window.addEventListener('resize', adjustSidebarHeight)

    return () => {
      window.removeEventListener('resize', adjustSidebarHeight)
    }
  }, [])
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------ Callback fucntions start ------------------------ */
  const handleDrawer = (): void => {
    setOpen(prevOpen => !prevOpen)
  }
  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const updateScreenSize = (): void => {
    setScreenSize(window.innerWidth)
  }

  const navigateTo = (path: string): void => {
    history.push(path)
  }

  const handleKeyNavigation = (event: KeyboardEvent<HTMLLIElement>, path: string): void => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }
    event.preventDefault()
    navigateTo(path)
  }

  const sideList = (): React.ReactNode => {
    const currentPath = history.location.pathname
    return (
      <div id="sideBarList" ref={sidebarRef} style={{ overflowY: 'auto', overflowX: 'hidden' }}>
        {sideBarItems?.map((listItem, index) => (
          <div key={index}>
            {listItem.subMenu ? (
              <Accordion defaultExpanded={false}>
                <AccordionSummary id={`accordion-${listItem.id}`}>
                  <ExpandMoreIcon />
                  <Typography style={{ fontWeight: 'bold', fontSize: '14px' }}>
                    {listItem.name}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    {listItem.subMenu.map((subMenuItem, subIndex) => (
                      <ListItem
                        key={subIndex}
                        style={{
                          padding: '5px',
                          backgroundColor: currentPath === subMenuItem.path ? '#16a9e4' : 'inherit',
                        }}
                        id={`subListItem-${listItem.id}-${subIndex}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => navigateTo(subMenuItem.path)}
                        onKeyDown={event => handleKeyNavigation(event, subMenuItem.path)}
                      >
                        <ListItemButton
                          style={{ justifyContent: 'left', marginLeft: '5px' }}
                          component="div"
                        >
                          <Typography
                            style={{
                              fontWeight: 'bold',
                              fontSize: '14px',
                              color: currentPath === subMenuItem.path ? 'whitesmoke' : 'black',
                            }}
                          >
                            {subMenuItem.name}
                          </Typography>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ) : (
              <ListItem
                style={{
                  padding: '5px',
                  backgroundColor: currentPath === listItem.path ? '#16a9e4' : 'inherit',
                }}
                id={`listItem-${listItem.id}`}
                role="button"
                tabIndex={0}
                onClick={() => navigateTo(listItem.path)}
                onKeyDown={event => handleKeyNavigation(event, listItem.path)}
              >
                <ListItemButton
                  style={{ justifyContent: 'left', marginLeft: '5px' }}
                  component="div"
                >
                  <Typography
                    style={{
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: currentPath === listItem.path ? 'whitesmoke' : 'black',
                    }}
                  >
                    {listItem.name}
                  </Typography>
                </ListItemButton>
              </ListItem>
            )}
            <Divider />
          </div>
        ))}
      </div>
    )
  }
  /* -------------------------- Render constants end -------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <div>
      <Drawer
        sx={{
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            top: 70,
          },
        }}
        open={openSidebar}
        anchor="left"
        onClose={handleDrawer}
        variant="persistent"
        style={{ position: 'relative' }}
      >
        {sideList()}
      </Drawer>
    </div>
  )
}
